import { ApolloError, useMutation } from '@apollo/client';
import { Alert, AlertTitle, Box, Button, Link, Typography } from '@mui/material';
import { useEffect, useRef, type ComponentType, type FC, type PropsWithChildren } from 'react';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { useLocation } from 'react-router-dom';
import SEND_TEAMS_MESSAGE from 'src/graphql/mutations/sendTeamsMessage';

const SHOW_DETAILED_ERROR = import.meta.env.DEV;

const PageFallback = (props: FallbackProps) => {
  return (
    <Box
      display="grid"
      height="100%"
      px={2}
      sx={{
        alignContent: 'center',
        placeItems: 'center',
      }}
    >
      <svg
        style={{ maxWidth: 300, width: '60%' }}
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 40.96668 49.16565"
      >
        <g fill="white">
          <path
            d="M17.48192,19.1671c3.21726,0,3.22259-5,0-5-3.21726,0-3.22259,5,0,5h0Z"
            origin="undraw"
          />
          <path d="M23.91104,19.1671c3.21726,0,3.22259-5,0-5-3.21726,0-3.22259,5,0,5h0Z" />
          <path d="M4.86666,47.34178c1.03481-6.33217,2.41234-12.60457,4.1499-18.78141,1.68242-5.98078,3.56619-12.04924,6.08151-17.73907,.83618-1.89148,1.89304-4.08154,3.7105-5.21024,1.87606-1.16509,4.09859-.54121,5.74563,.75122,2.02908,1.59223,3.13082,4.1372,3.97571,6.50067,1.07302,3.00165,1.99225,6.06607,2.85992,9.1325,2.02506,7.15672,3.58988,14.43638,4.71278,21.78832,.20403,1.33585,1.88291,2.07378,3.0753,1.7461,1.42757-.39232,1.95074-1.73544,1.7461-3.0753-1.08669-7.11489-2.58733-14.16153-4.5181-21.09558-.95543-3.43124-1.96641-6.87098-3.19563-10.21555-1.07391-2.92199-2.44398-5.88746-4.78399-8.024C26.00138,.90541,22.75992-.4132,19.46016,.11703c-2.85236,.45834-5.07725,2.34491-6.71206,4.63273-1.75954,2.46235-2.78587,5.415-3.85088,8.22124-1.22136,3.21821-2.34523,6.47332-3.36865,9.75985C3.15534,30.35198,1.33252,38.1356,.04526,46.01258c-.21751,1.33096,.34643,2.69071,1.7461,3.0753,1.19398,.32807,2.85629-.40597,3.0753-1.7461h0Z" />
        </g>
      </svg>
      <Typography variant="h2" maxWidth={700} textAlign="center" mt={4} mb={1}>
        Ups! Algo salió mal.
      </Typography>
      {import.meta.env.DEV ? (
        <pre>{props.error.stack}</pre>
      ) : (
        <Typography
          fontSize={{ xs: 18, sm: 22, md: 24 }}
          maxWidth={700}
          color="#A0AEC0"
          textAlign="center"
        >
          Estamos trabajando para solucionarlo. Si el problema persiste comuníquese con soporte.{' '}
          <Link href="mailto:soporte@captahydro.com">soporte@captahydro.com</Link>
        </Typography>
      )}
    </Box>
  );
};

const SectionAlertFallback = (props: FallbackProps) => {
  let message: string | JSX.Element | JSX.Element[] = 'Error';

  if (props.error instanceof ApolloError) {
    const { graphQLErrors } = props.error;
    const errors = graphQLErrors.map((error) => ({
      code: (error.extensions?.code as string) || null,
      message: error.message,
    }));
    // TODO: improve
    message = errors.map(({ code, message }, key) => {
      return (
        <Typography key={key} fontSize="inherit">
          {code}: {message}
        </Typography>
      );
    });
  } else if (props.error instanceof Error) {
    message = props.error.message;
  }

  return (
    <Alert
      severity="error"
      action={
        <Button color="inherit" onClick={() => props.resetErrorBoundary()}>
          Retry
        </Button>
      }
    >
      <AlertTitle>Error</AlertTitle>
      No se ha podido cargar esta sección. Si el error persiste comuníquese con soporte.{' '}
      <Link href="mailto:soporte@captahydro.com">soporte@captahydro.com</Link>
      {SHOW_DETAILED_ERROR && (
        <Box mt={1} fontSize={12}>
          {message}
        </Box>
      )}
    </Alert>
  );
};

const ModalFallback = (props: FallbackProps) => {
  let message: string | JSX.Element | JSX.Element[] = 'Error';

  if (props.error instanceof ApolloError) {
    const { graphQLErrors } = props.error;
    const errors = graphQLErrors.map((error) => ({
      code: (error.extensions?.code as string) || null,
      message: error.message,
    }));
    // TODO: improve, remove duplicated code
    message = errors.map(({ code, message }, key) => {
      return (
        <Typography fontSize="inherit" key={key}>
          {code}: {message}
        </Typography>
      );
    });
  } else if (props.error instanceof Error) {
    message = props.error.message;
  }

  return (
    <Box px={2} py={2}>
      <Alert severity="error">
        <AlertTitle>Error</AlertTitle>
        Hubo un error al cargar el modal. Si el error persiste comuníquese con soporte.{' '}
        <Link href="mailto:soporte@captahydro.com">soporte@captahydro.com</Link>
        {SHOW_DETAILED_ERROR && (
          <Box mt={1} fontSize={12}>
            {message}
          </Box>
        )}
      </Alert>
    </Box>
  );
};

const DefaultFallback: FC<PropsWithChildren<FallbackProps & { componentStack?: string }>> = ({
  children,
  componentStack,
  ...props
}) => {
  const { pathname } = useLocation();

  const [sendError] = useMutation(SEND_TEAMS_MESSAGE);

  useEffect(() => {
    if (!props.error.message || !pathname || !sendError) return;

    // debounce
    const token = setTimeout(async () => {
      try {
        await sendError({
          variables: {
            input: {
              path: pathname,
              message: props.error.stack || props.error.message,
              errorName: props.error.name,
              errorMessage: props.error.message,
              componentStack,
              userAgent: navigator.userAgent,
            },
          },
        });
      } catch (e) {
        // No relanzar — las notificaciones no deben interrumpir la pantalla de error
        console.warn('Teams: error notification failed:', e);
      }
    }, 1000);
    return () => clearTimeout(token);
  }, [props.error.message, props.error.stack, pathname, sendError, componentStack]);

  return children;
};

interface Props extends PropsWithChildren {
  fallback?: ComponentType<FallbackProps> | null;
  /**
   * Set a template for the error render component
   * @default 'fullpage'
   */
  variant?: 'fullpage' | 'section-alert' | 'modal';
}

const FallbackMap = {
  fullpage: PageFallback,
  'section-alert': SectionAlertFallback,
  modal: ModalFallback,
};

const ApolloErrorBoundary: FC<Props> = ({ children, fallback, variant }) => {
  const { pathname } = useLocation();
  const componentStackRef = useRef<string>('');

  const Fallback = fallback || (variant && FallbackMap[variant]) || SectionAlertFallback;

  return (
    // change key to force the error state to reset whenever pathname changes
    <ErrorBoundary
      key={pathname}
      onError={(_error, info) => {
        componentStackRef.current = info.componentStack ?? '';
      }}
      FallbackComponent={(props) => (
        <DefaultFallback {...props} componentStack={componentStackRef.current}>
          {Fallback ? <Fallback {...props} /> : null}
        </DefaultFallback>
      )}
    >
      {children}
    </ErrorBoundary>
  );
};
export default ApolloErrorBoundary;
